import { apiCall } from "@/shared/services/apiCall";
import { errorToast, successToast } from "@/shared/services/helper";
import store from '@/store';
import { reactive } from "vue";

const auth = reactive({ status: store.getters.isAuthenticated, user: store.getters.stateUser });

/**
 * Handles the password change process by sending the old and new passwords to the backend API.
 * Optionally includes a withdraw password for certain business types and first-time logins.
 * Updates user state and login IDs in the Vuex store on successful password change.
 * Displays appropriate success or error toasts based on the API response.
 * @param {string} oldPassword - The current password of the user.
 * @param {string} newPassword - The new password to set.
 * @param {string} confirmPassword - The confirmation of the new password.
 * @param {string} withdrawPassword - The withdraw password (if required).
 * @returns {Promise<object>} An object with either the updated user data or an error message.
 */
const callChangePasswordServiceFunction = async(oldPassword, newPassword, confirmPassword) => {
    let returnObj = {
            userData: null,
            changeErrorRes: null
    }
    try{
        const changePasswordForm = new FormData();
        changePasswordForm.append("current_password", oldPassword);
        changePasswordForm.append("password", newPassword);
        changePasswordForm.append("confirm_password", confirmPassword);
        
        let response = await apiCall('post','CHANGE_MY_PASSWORD', changePasswordForm);
        if (response && response.status == 200) {
            if (response?.data?.status == 0 || response?.data?.error || response?.data?.msg) {
                if(response.data.msg){
                    returnObj.changeErrorRes = 'Something went wrong';
                } else {
                    returnObj.changeErrorRes = response?.data?.error;
                }
                errorToast(response?.data?.error);
            } else {
                // if (store.getters.siteSettings?.business_type == 2 && !auth?.user?.last_login_at && withdrawPassword && !auth?.user?.phone) {
                //     let withdrawErr = await callUpdateWithdrawPassword(withdrawPassword);
                //     if (withdrawErr) {
                //         returnObj.changeErrorRes = withdrawErr;
                //         errorToast(withdrawErr);
                //         return returnObj;
                //     }
                // };
                successToast(response?.data?.success);
                let user = auth.user;
                user.is_fresh = 0;
                user.is_password_secure = 1;
                user.is_withDraw_password = true;
                // Userid and offer-----------------------------------------------------------
                setUserIdsToStore();
                // ---------------------------------------------------------------------------
                if(!store.getters?.stateUser?.last_login_at){ 
                    let userDetails = store.getters?.stateUser;
                    userDetails.last_login_at = parseInt(Date.now()/1000);
                    store.dispatch('setUser', userDetails);
                }
            }
        }
        else{
            returnObj.changeErrorRes = response?.data?.error || 'Something went wrong';
        }
        return returnObj;   
    }
    catch(error){
        console.log(error);
        return {
            changeErrorRes: error || 'Something went wrong'
        };
    }
};

/**
 * Updates the user's transaction/withdraw password by calling the wallet API.
 * This function is typically called after a successful password change for users
 * who are setting up their withdraw password for the first time.
 * 
 * @param {string} withdrawPassword - The new withdraw/transaction password to set
 * @returns {Promise<string|undefined>} Returns an error message string if the update fails, 
 *         or undefined if the update is successful 
 * 
 */
const callUpdateWithdrawPassword = async (withdrawPassword) => {
    try{
        let response = await apiCall('post','UPDATE_TXN_PASSWORD', {txn_password: withdrawPassword}, 'wallet');
        if (!response || response.status !== 200) {
            return response?.data?.error || 'Something went wrong';
        }
        return undefined;
    } catch(error){
        console.error(error);
        return error[0] || 'Something went wrong';
    }
}

// Userid and offer-----------------------------------------------------------
/**
 * Updates the Vuex store with the current user's ID after a password change.
 * Maintains the login method flag and dispatches the updated login data to the store.
 * @returns {void}
 */
const setUserIdsToStore = async() => {
    let userid = auth?.user?.userid
    let oldUserIds = store.getters?.logInData?.userIds || [];
    let loginWithMobileFromAuth  = store.getters?.logInData?.logInWithMobile
    let data = {
        logInWithMobile: loginWithMobileFromAuth,
        userIds: oldUserIds
    }
    store.dispatch('setLogInData', data);
};

const onUpdateWithdrawTransactionPassword = async (withdrawPassword) => {
    try{
        let response = await apiCall('post','UPDATE_TRANSACTION_PASSWORD', { txn_password: withdrawPassword }, 'wallet');
        if (!response || response.status !== 200) {
            return { error :response?.data?.error || 'Something went wrong' };
        }
        return { error : null , message : response?.data?.message || response?.message  };
    } catch(err){
        console.error(err);
        return { error :err[0] || 'Something went wrong' };
    }
}

export {
    callChangePasswordServiceFunction,
    onUpdateWithdrawTransactionPassword
}